// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { useEffect, useState } from "react";
import {
  getDocs,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { auth } from "../firebase";
import { useClient } from "../contexts/ClientContext";
import { timed, perfMark, perfTotal } from "../utils/perf";

import {
  Box,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
  Typography,
  Snackbar,
  Alert,
  SelectChangeEvent,
  Paper,
  Divider,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Avatar,
  TablePagination,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SendIcon from "@mui/icons-material/Send";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import SummaryCard from "../components/SummaryCard";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import usePagination from "../hooks/usePagination";

interface Employee {
  empId: string;
  firstName: string;
  lastName: string;
  fullName: string;
}
interface LeaveRow {
  id: string;
  empId: string;
  startDate: string;
  endDate: string;
  type: string;
  status: string;
  reason: string;
  createdAt: Timestamp;
}

export default function LeavePage() {
  const { col } = useClient();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [empId, setEmpId] = useState("");
  const [leaveStart, setLeaveStart] = useState("");
  const [leaveEnd, setLeaveEnd] = useState("");
  const [leaveType, setLeaveType] = useState("paid");
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveRequests, setLeaveRequests] = useState<LeaveRow[]>([]);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState<"success" | "error" | "info">("success");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const today = dayjs().format("YYYY-MM-DD");

  const showMsg = (text: string, type: "success" | "error" | "info" = "success") => {
    setMsg(text);
    setMsgType(type);
  };

  const getEmpName = (id: string) => {
    const emp = employees.find((e) => e.empId === id);
    return emp ? (emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.fullName) : id;
  };

  useEffect(() => {
    async function load() {
      const t0 = performance.now();
      try {
        const [empSnap, snap] = await Promise.all([
          timed("LeavePage.load.employees", () => getDocs(col("employees"))),
          timed("LeavePage.load.leaveRequests", () => getDocs(col("leaveRequests"))),
        ]);
        setEmployees(
          empSnap.docs.map((d) => {
            const data = d.data();
            return {
              empId: d.id,
              firstName: (data.firstName as string) || "",
              lastName: (data.lastName as string) || "",
              fullName: (data.fullName as string) || `${data.firstName || ""} ${data.lastName || ""}`.trim() || d.id,
            };
          })
        );
        setLeaveRequests(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<LeaveRow, "id">) }))
        );
        perfTotal("LeavePage.load TOTAL", t0);
      } catch {
        showMsg("Failed to load data.", "error");
      }
    }
    load();
  }, []);

  const submitLeave = async () => {
    if (!empId) return showMsg("Please select an employee.", "error");
    if (!leaveStart) return showMsg("Please select a start date.", "error");
    if (!leaveEnd) return showMsg("Please select an end date.", "error");
    if (leaveEnd < leaveStart) return showMsg("End date cannot be before start date.", "error");
    if (dayjs(today).diff(dayjs(leaveStart), "day") > 30) return showMsg("Leave start date cannot be more than 30 days in the past.", "error");
    const done = perfMark(`LeavePage.submitLeave(${empId})`);
    try {
      await addDoc(col("leaveRequests"), {
        empId,
        startDate: leaveStart,
        endDate: leaveEnd,
        type: leaveType,
        reason: leaveReason.trim(),
        status: "pending",
        requestedBy: auth.currentUser?.uid ?? "",
        createdAt: Timestamp.now(),
      });
      done();
      const days = dayjs(leaveEnd).diff(dayjs(leaveStart), "day") + 1;
      showMsg(`Leave request submitted for ${days} day(s) — pending admin approval.`);
      setLeaveStart("");
      setLeaveEnd("");
      setLeaveReason("");
      // Refresh
      const snap = await timed("LeavePage.refreshLeaveRequests", () => getDocs(col("leaveRequests")));
      setLeaveRequests(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<LeaveRow, "id">) })));
    } catch {
      showMsg("Failed to submit leave request.", "error");
    }
  };

  // Count stats
  const pendingCount = leaveRequests.filter((l) => l.status === "pending").length;
  const approvedCount = leaveRequests.filter((l) => l.status === "approved").length;

  // Filtered leave list
  const filteredLeaves = leaveRequests.filter((l) => {
    if (filterStatus && l.status !== filterStatus) return false;
    if (filterEmployee && l.empId !== filterEmployee) return false;
    if (filterDateFrom && l.endDate < filterDateFrom) return false;
    if (filterDateTo && l.startDate > filterDateTo) return false;
    return true;
  }).sort((a, b) => (a.status === "pending" ? -1 : 1));

  const lvPag = usePagination(filteredLeaves);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 800 }}>
        Leave Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Submit and track leave requests for employees.
      </Typography>

      {/* Stats */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <SummaryCard icon={<HourglassEmptyIcon />} value={pendingCount} label="Pending" color="#F9A825" />
        <SummaryCard icon={<CheckCircleIcon />} value={approvedCount} label="Approved" color="#1565C0" />
        <SummaryCard icon={<FormatListNumberedIcon />} value={leaveRequests.length} label="Total Requests" color="#2E7D32" />
      </Box>

      {/* Leave Request Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <EventNoteIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>New Leave Request</Typography>
        </Box>
        <Divider sx={{ mb: 2.5 }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl sx={{ minWidth: 250, flex: 1 }}>
              <InputLabel>Employee</InputLabel>
              <Select
                value={empId}
                label="Employee"
                onChange={(e: SelectChangeEvent) => setEmpId(e.target.value)}
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.empId} value={emp.empId}>
                    {emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.fullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={leaveType}
                label="Type"
                onChange={(e: SelectChangeEvent) => setLeaveType(e.target.value)}
              >
                <MenuItem value="paid">Paid Leave</MenuItem>
                <MenuItem value="unpaid">Unpaid Leave</MenuItem>
                <MenuItem value="sick">Sick Leave</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <DatePicker
              label="Start Date"
              value={leaveStart ? dayjs(leaveStart) : null}
              onChange={(v: Dayjs | null) => setLeaveStart(v ? v.format("YYYY-MM-DD") : "")}
              format="DD/MMM/YYYY"
              minDate={dayjs().subtract(30, "day")}
              slotProps={{ textField: { sx: { minWidth: 180, flex: 1 } } }}
            />
            <DatePicker
              label="End Date"
              value={leaveEnd ? dayjs(leaveEnd) : null}
              onChange={(v: Dayjs | null) => setLeaveEnd(v ? v.format("YYYY-MM-DD") : "")}
              format="DD/MMM/YYYY"
              minDate={dayjs().subtract(30, "day")}
              slotProps={{ textField: { sx: { minWidth: 180, flex: 1 } } }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Reason (optional)"
            value={leaveReason}
            onChange={(e) => setLeaveReason(e.target.value)}
          />
          <Button
            variant="contained"
            size="large"
            onClick={submitLeave}
            startIcon={<SendIcon />}
            sx={{ alignSelf: "flex-start" }}
          >
            Submit Leave Request
          </Button>
        </Box>
      </Paper>

      {/* Leave History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Leave History</Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={(e: SelectChangeEvent) => { setFilterStatus(e.target.value); lvPag.resetPage(); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Employee</InputLabel>
            <Select value={filterEmployee} label="Employee" onChange={(e: SelectChangeEvent) => { setFilterEmployee(e.target.value); lvPag.resetPage(); }}>
              <MenuItem value="">All Employees</MenuItem>
              {employees.map((emp) => (
                <MenuItem key={emp.empId} value={emp.empId}>
                  {emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : emp.fullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DatePicker
            label="From"
            value={filterDateFrom ? dayjs(filterDateFrom) : null}
            onChange={(v: Dayjs | null) => { setFilterDateFrom(v ? v.format("YYYY-MM-DD") : ""); lvPag.resetPage(); }}
            format="DD/MMM/YYYY"
            slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
          />
          <DatePicker
            label="To"
            value={filterDateTo ? dayjs(filterDateTo) : null}
            onChange={(v: Dayjs | null) => { setFilterDateTo(v ? v.format("YYYY-MM-DD") : ""); lvPag.resetPage(); }}
            format="DD/MMM/YYYY"
            slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
          />
          {(filterStatus || filterEmployee || filterDateFrom || filterDateTo) && (
            <Button size="small" onClick={() => { setFilterStatus(""); setFilterEmployee(""); setFilterDateFrom(""); setFilterDateTo(""); lvPag.resetPage(); }}>
              Clear Filters
            </Button>
          )}
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell align="center">Days</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLeaves.length === 0 && (
                <TableRow>
                  <TableCell colSpan={99} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    <EventNoteIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} /><br />
                    No leave requests found.
                  </TableCell>
                </TableRow>
              )}
              {lvPag.paginatedItems.map((l) => {
                  const days = dayjs(l.endDate).diff(dayjs(l.startDate), "day") + 1;
                  return (
                    <TableRow key={l.id} sx={{ bgcolor: l.status === "pending" ? "warning.light" : l.status === "approved" ? "success.light" : l.status === "rejected" ? "error.light" : "inherit" }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: "0.75rem", bgcolor: "secondary.main" }}>
                            {getEmpName(l.empId).charAt(0)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{getEmpName(l.empId)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>{l.type}</TableCell>
                      <TableCell>{dayjs(l.startDate).format("DD/MMM/YYYY")}</TableCell>
                      <TableCell>{dayjs(l.endDate).format("DD/MMM/YYYY")}</TableCell>
                      <TableCell align="center">
                        <Chip label={`${days}d`} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap>{l.reason || "—"}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={l.status}
                          size="small"
                          color={l.status === "approved" ? "success" : l.status === "rejected" ? "error" : "warning"}
                          sx={{ textTransform: "capitalize", fontWeight: 600 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredLeaves.length > 0 && (
          <TablePagination component="div" count={lvPag.totalCount} page={lvPag.page} onPageChange={lvPag.onPageChange} rowsPerPage={lvPag.rowsPerPage} onRowsPerPageChange={lvPag.onRowsPerPageChange} rowsPerPageOptions={[10, 25, 50]} />
        )}
      </Paper>

      <Snackbar open={!!msg} autoHideDuration={4000} onClose={() => setMsg("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={msgType} variant="filled" onClose={() => setMsg("")}>{msg}</Alert>
      </Snackbar>
    </Box>
  );
}
